import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../ui/select";
import { Card } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Clock,
  Calendar,
  Loader2,
  Save,
  Wand2,
  Copy,
  BookOpen,
} from "lucide-react";
import { MATIERES, getMatiereInfo } from "./constants";
import { getTimetable, saveTimetable, getWeekSchedule } from "../../lib/api";

// Days of the week
const DAYS = [
  { id: "lundi", label: "Lundi" },
  { id: "mardi", label: "Mardi" },
  { id: "mercredi", label: "Mercredi" },
  { id: "jeudi", label: "Jeudi" },
  { id: "vendredi", label: "Vendredi" },
  { id: "samedi", label: "Samedi" },
];

// Default time options
const TIME_OPTIONS = [];
for (let h = 8; h <= 18; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_OPTIONS.push(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);
  }
}

// Session types
const SESSION_TYPES = [
  { value: "decouverte", label: "Découverte" },
  { value: "apprentissage", label: "Apprentissage" },
  { value: "entrainement", label: "Entraînement" },
  { value: "evaluation", label: "Évaluation" },
  { value: "remediation", label: "Remédiation" },
  { value: "projet", label: "Projet" },
];

/**
 * Single timetable entry component
 */
function TimetableEntry({ entry, index, onUpdate, onDelete, workingDays }) {
  const matiereInfo = getMatiereInfo(entry.matiere);
  
  return (
    <Card 
      className="p-3 relative group"
      style={{ borderLeftColor: matiereInfo?.color || "#64748B", borderLeftWidth: 4 }}
    >
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
        onClick={onDelete}
      >
        <Trash2 className="w-3 h-3 text-red-500" />
      </Button>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {/* Day */}
        <div>
          <Label className="text-xs text-slate-500">Jour</Label>
          <Select value={entry.day} onValueChange={(v) => onUpdate({ ...entry, day: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAYS.filter(d => workingDays.includes(d.id)).map((day) => (
                <SelectItem key={day.id} value={day.id}>{day.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Start time */}
        <div>
          <Label className="text-xs text-slate-500">Début</Label>
          <Select value={entry.start_time} onValueChange={(v) => onUpdate({ ...entry, start_time: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_OPTIONS.map((time) => (
                <SelectItem key={time} value={time}>{time}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* End time */}
        <div>
          <Label className="text-xs text-slate-500">Fin</Label>
          <Select value={entry.end_time} onValueChange={(v) => onUpdate({ ...entry, end_time: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_OPTIONS.map((time) => (
                <SelectItem key={time} value={time}>{time}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Matière */}
        <div>
          <Label className="text-xs text-slate-500">Matière</Label>
          <Select value={entry.matiere} onValueChange={(v) => onUpdate({ ...entry, matiere: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MATIERES.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                    {m.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-2 mt-2">
        {/* Title template */}
        <div>
          <Label className="text-xs text-slate-500">Titre par défaut</Label>
          <Input
            value={entry.title_template || ""}
            onChange={(e) => onUpdate({ ...entry, title_template: e.target.value })}
            placeholder="Ex: Lecture"
            className="h-8 text-xs"
          />
        </div>
        
        {/* Session type */}
        <div>
          <Label className="text-xs text-slate-500">Type de séance</Label>
          <Select value={entry.session_type || "apprentissage"} onValueChange={(v) => onUpdate({ ...entry, session_type: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SESSION_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Week type */}
      <div className="mt-2 flex items-center gap-2">
        <Label className="text-xs text-slate-500">Semaine :</Label>
        <div className="flex gap-1">
          <Button
            variant={entry.week_type === "all" ? "default" : "outline"}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onUpdate({ ...entry, week_type: "all" })}
          >
            Toutes
          </Button>
          <Button
            variant={entry.week_type === "pair" ? "default" : "outline"}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onUpdate({ ...entry, week_type: "pair" })}
          >
            Paires
          </Button>
          <Button
            variant={entry.week_type === "impair" ? "default" : "outline"}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onUpdate({ ...entry, week_type: "impair" })}
          >
            Impaires
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Day schedule view
 */
function DaySchedule({ day, entries, workingDays, onAddEntry, onUpdateEntry, onDeleteEntry }) {
  const dayEntries = entries.filter(e => e.day === day.id);
  const sortedEntries = dayEntries.sort((a, b) => a.start_time.localeCompare(b.start_time));
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-sm">{day.label}</h3>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onAddEntry(day.id)}
        >
          <Plus className="w-3 h-3 mr-1" />
          Ajouter
        </Button>
      </div>
      
      {sortedEntries.length === 0 ? (
        <div className="text-center py-4 text-sm text-slate-400 border-2 border-dashed rounded-lg">
          Aucun créneau défini
        </div>
      ) : (
        <div className="space-y-2">
          {sortedEntries.map((entry, idx) => (
            <TimetableEntry
              key={entry.id || idx}
              entry={entry}
              index={idx}
              workingDays={workingDays}
              onUpdate={(updated) => onUpdateEntry(entry.id, updated)}
              onDelete={() => onDeleteEntry(entry.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Main Timetable Dialog Component
 */
export default function TimetableDialog({ open, onOpenChange, onTimetableSaved }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workingDays, setWorkingDays] = useState(["lundi", "mardi", "jeudi", "vendredi"]);
  const [activeTab, setActiveTab] = useState("lundi");
  
  // Fetch timetable and config
  useEffect(() => {
    if (!open) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [timetableRes, configRes] = await Promise.all([
          getTimetable(),
          getWeekSchedule(),
        ]);
        
        if (timetableRes.data?.entries) {
          setEntries(timetableRes.data.entries);
        }
        
        if (configRes.data?.working_days) {
          setWorkingDays(configRes.data.working_days);
          // Set active tab to first working day
          if (configRes.data.working_days.length > 0) {
            setActiveTab(configRes.data.working_days[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching timetable:", err);
        toast.error("Erreur lors du chargement de l'emploi du temps");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [open]);
  
  // Add new entry
  const handleAddEntry = (day) => {
    const newEntry = {
      id: `entry_${Date.now()}`,
      day,
      start_time: "08:30",
      end_time: "09:15",
      matiere: "francais",
      title_template: "",
      session_type: "apprentissage",
      week_type: "all", // all, pair, impair
    };
    setEntries([...entries, newEntry]);
  };
  
  // Update entry
  const handleUpdateEntry = (entryId, updated) => {
    setEntries(entries.map(e => e.id === entryId ? { ...e, ...updated } : e));
  };
  
  // Delete entry
  const handleDeleteEntry = (entryId) => {
    setEntries(entries.filter(e => e.id !== entryId));
  };
  
  // Duplicate day
  const handleDuplicateDay = (fromDay, toDay) => {
    const fromEntries = entries.filter(e => e.day === fromDay);
    const newEntries = fromEntries.map(e => ({
      ...e,
      id: `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      day: toDay,
    }));
    setEntries([...entries, ...newEntries]);
    toast.success(`Créneaux de ${fromDay} copiés vers ${toDay}`);
  };
  
  // Save timetable
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTimetable({ entries });
      toast.success("Emploi du temps enregistré");
      onTimetableSaved?.();
      onOpenChange(false);
    } catch (err) {
      console.error("Error saving timetable:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };
  
  // Calculate stats
  const stats = useMemo(() => {
    const byMatiere = {};
    entries.forEach(e => {
      const [startH, startM] = e.start_time.split(":").map(Number);
      const [endH, endM] = e.end_time.split(":").map(Number);
      const duration = (endH * 60 + endM) - (startH * 60 + startM);
      
      if (!byMatiere[e.matiere]) byMatiere[e.matiere] = 0;
      byMatiere[e.matiere] += duration;
    });
    return byMatiere;
  }, [entries]);
  
  const visibleDays = DAYS.filter(d => workingDays.includes(d.id));
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Emploi du temps
          </DialogTitle>
          <DialogDescription>
            Définissez les créneaux récurrents pour générer automatiquement les séances
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <>
            {/* Weekly hours summary */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <div className="text-xs font-medium text-slate-600 mb-2">Heures par semaine :</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats).map(([matiere, minutes]) => {
                  const info = getMatiereInfo(matiere);
                  const hours = Math.floor(minutes / 60);
                  const mins = minutes % 60;
                  return (
                    <Badge 
                      key={matiere}
                      variant="outline"
                      style={{ borderColor: info?.color, color: info?.color }}
                      className="text-xs"
                    >
                      {info?.label}: {hours}h{mins > 0 ? mins.toString().padStart(2, "0") : ""}
                    </Badge>
                  );
                })}
                {Object.keys(stats).length === 0 && (
                  <span className="text-xs text-slate-400">Aucun créneau défini</span>
                )}
              </div>
            </div>
            
            {/* Day tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between mb-3">
                <TabsList>
                  {visibleDays.map((day) => (
                    <TabsTrigger key={day.id} value={day.id} className="text-xs">
                      {day.label}
                      {entries.filter(e => e.day === day.id).length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-[10px] justify-center">
                          {entries.filter(e => e.day === day.id).length}
                        </Badge>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {/* Copy day dropdown */}
                <Select onValueChange={(toDay) => handleDuplicateDay(activeTab, toDay)}>
                  <SelectTrigger className="w-auto h-8 text-xs">
                    <Copy className="w-3 h-3 mr-1" />
                    Copier vers...
                  </SelectTrigger>
                  <SelectContent>
                    {visibleDays.filter(d => d.id !== activeTab).map((day) => (
                      <SelectItem key={day.id} value={day.id}>{day.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {visibleDays.map((day) => (
                <TabsContent key={day.id} value={day.id}>
                  <DaySchedule
                    day={day}
                    entries={entries}
                    workingDays={workingDays}
                    onAddEntry={handleAddEntry}
                    onUpdateEntry={handleUpdateEntry}
                    onDeleteEntry={handleDeleteEntry}
                  />
                </TabsContent>
              ))}
            </Tabs>
            
            {/* Actions */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
